import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Mail, CheckCircle2, Check, X, Zap } from "lucide-react";
import axios from "axios";
import { useSeo } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/credits/packages`;
const STEPS = [[MapPin, "Velg område og bransjer", "Skriv inn et sted, velg avstand og bransjer – kontorer, sameier, barnehager, butikker, klinikker, verksteder og mer."],
  [Search, "Vi finner bedriftene", "Verktøyet søker i offentlige kilder som Google Places og Brønnøysundregistrene, fjerner duplikater og viser tilgjengelig kontaktinfo."],
  [Mail, "Godkjenn personlige utkast", "Du får e-postutkast basert på faktisk informasjon om hver bedrift. Ingenting sendes før du har lest og godkjent."]];
const FAQ = [
  ["Hva er én kreditt?", "Én kreditt = ett gjennomført søk. Standard finner opptil 20 bedrifter per søk, Pro flere. Du betaler aldri per bedrift."],
  ["Hva skjer hvis søket ikke finner noe?", "Søk som feiler eller ikke gir resultater koster ingenting."],
  ["Er det abonnement?", "Nei. Alle pakker er engangskjøp uten månedlige trekk. Kreditter du har kjøpt, utløper ikke."],
  ["Hvordan låser jeg opp Pro?", "Pro låses opp når du kjøper 300, 600 eller 1200 kreditter."],
  ["Sendes e-post automatisk?", "Nei. Du leser, redigerer og godkjenner hver e-post før sending. Hver e-post viser hvem som sender, og mottakeren kan enkelt reservere seg."],
  ["Er det lov å sende e-post til bedrifter?", "Markedsføringsloven § 15 krever samtykke for e-post til privatpersoner. Til bedrifter bør henvendelsen være relevant, saklig og sendt til bedriftens adresse. Respekter alltid reservasjoner. Dette er ikke juridisk rådgivning."],
  ["Garanterer dere antall treff?", "Nei. 20 eller flere bedrifter per søk er et maksimum. Hvor mange som finnes avhenger av område og bransje."],
];
const Row = ({ label, std, pro }) => (
  <tr className="border-b border-stone-100 last:border-0">
    <td className="py-2.5 pr-3 text-sm">{label}</td>
    {[std, pro].map((v, i) => <td key={i} className="py-2.5 text-center text-sm">{v === true ? <Check className="mx-auto h-4 w-4 text-[#2F5D50]" /> : v === false ? <X className="mx-auto h-4 w-4 text-stone-300" /> : v}</td>)}
  </tr>
);

const PackageCard = ({ k, std, base }) => {
  const hot = !!k.badge;
  const per = k.price / k.credits;
  const save = Math.round((1 - per / (base.price / base.credits)) * 100);
  return (
    <div data-testid={`pf-package-${k.id}`} className={`relative flex flex-col rounded-2xl p-6 hover:-translate-y-1 transition-transform ${k.id === "p300" ? "bg-[#2F5D50] text-white shadow-xl ring-2 ring-[#C9E4CF]" : "border border-stone-200 bg-white"}`}>
      {hot && <span className="absolute -top-3 left-6 rounded-full bg-[#C9E4CF] px-3 py-0.5 text-xs font-bold text-[#1F3D35]">{k.badge}</span>}
      <div className={`text-sm font-bold ${k.id === "p300" ? "text-[#C9E4CF]" : "text-stone-500"}`}>{k.credits} SØK{k.unlocks_pro ? " · PRO" : " · STANDARD"}</div>
      <div className="mt-2 font-heading text-3xl font-extrabold">{k.price.toLocaleString("nb-NO")} kr</div>
      <div className={`mt-1 text-sm font-semibold ${k.id === "p300" ? "text-white/90" : "text-[#2F5D50]"}`}>
        {per.toFixed(2).replace(".", ",")} kr per søk{save > 0 ? ` · spar ${save} %` : ""}
      </div>
      <p className={`mt-3 text-sm ${k.id === "p300" ? "text-white/80" : "text-stone-600"}`}>Opptil {(k.credits * std).toLocaleString("nb-NO")} bedrifter med Standard.{k.unlocks_pro ? " Låser opp Pro." : ""}</p>
      <Link to="/login?mode=register" data-testid={`pf-buy-${k.id}`} className={`mt-5 rounded-full px-4 py-2 text-center text-sm font-bold transition-colors ${k.id === "p300" ? "bg-white text-[#1F3D35] hover:bg-[#C9E4CF]" : "border border-[#2F5D50] text-[#2F5D50] hover:bg-[#E7EFE9]"}`}>Velg</Link>
    </div>
  );
};

export default function ProspectFinderPage() {
  const [p, setP] = useState(null);
  useEffect(() => { axios.get(API).then((r) => setP(r.data)); }, []);
  useSeo({ title: "Prospect Finder – finn bedriftskunder for renholdsbedrifter", path: "/prospect-finder",
    description: "Verktøy for renholdsbedrifter: finn bedriftskunder i ditt område, se kontaktinfo og lag personlige e-postutkast. 5 gratis søk, ingen abonnement." });
  const std = p?.standard_limit ?? 20;
  const pro = p?.pro_limit ?? 100;

  return (
    <main data-testid="prospect-finder-page">
      <section className="bg-[#1F3D35] text-white">
        <div className="mx-auto max-w-6xl px-5 py-16 lg:py-24 grid gap-10 lg:grid-cols-[1.2fr_1fr] items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold tracking-wide">FOR RENHOLDSBEDRIFTER</span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">Finn nye bedriftskunder på minutter.</h1>
            <p className="mt-5 max-w-xl text-lg text-white/80">Prospect Finder finner kontorer, sameier, butikker og andre virksomheter i området ditt – og hjelper deg å skrive personlige henvendelser du selv godkjenner.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login?mode=register" data-testid="pf-start-free" className="rounded-full bg-[#C9E4CF] px-7 py-3.5 font-bold text-[#1F3D35] hover:bg-white transition-colors">Start {p?.free_searches ?? 5} gratis søk</Link>
              <Link to="/login" data-testid="pf-login" className="rounded-full border border-white/40 px-7 py-3.5 font-bold hover:bg-white/10 transition-colors">Logg inn</Link>
            </div>
            <p className="mt-4 text-xs text-white/60">Ingen kort kreves · Ingen abonnement · 1 kreditt = 1 søk</p>
          </motion.div>
          <div className="rounded-3xl border border-white/15 bg-white/5 p-6 backdrop-blur">
            <div className="flex items-center gap-2 text-sm font-semibold"><Zap className="h-4 w-4 text-[#C9E4CF]" />Eksempel på søk</div>
            <div className="mt-4 space-y-2 text-sm">
              {["Område: valgfritt sted + radius", "Bransjer: kontor, sameie, klinikk …", `Resultat: opptil ${std} bedrifter (Standard) / ${pro} (Pro)`, "Kostnad: 1 kreditt – uansett antall treff"].map((t) => (
                <div key={t} className="flex gap-2 rounded-xl bg-white/5 px-3 py-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-[#C9E4CF]" />{t}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16 grid gap-6 md:grid-cols-3">
        {STEPS.map(([Icon, t, d], i) => (
          <motion.div key={t} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
            className="rounded-2xl border border-stone-200 bg-white p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow]">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E7EFE9] text-[#2F5D50]"><Icon className="h-5 w-5" /></div>
            <h2 className="mt-4 font-heading text-lg font-bold">{i + 1}. {t}</h2>
            <p className="mt-1.5 text-sm text-stone-600">{d}</p>
          </motion.div>
        ))}
      </section>

      <section className="mx-auto max-w-4xl px-5 py-8">
        <h2 className="font-heading text-3xl font-extrabold">Standard og Pro</h2>
        <p className="mt-2 text-stone-600">Samme kredittmodell – Pro gir større søk og smartere resultater.</p>
        <div className="mt-6 overflow-x-auto rounded-2xl border border-stone-200 bg-white p-5" data-testid="pf-compare">
          <table className="w-full">
            <thead><tr className="text-sm"><th className="text-left pb-3"></th><th className="pb-3 font-heading">Standard</th><th className="pb-3 font-heading text-[#2F5D50]">Pro</th></tr></thead>
            <tbody>
              <Row label="Bedrifter per søk (maks)" std={`${std}`} pro={`${pro}`} />
              <Row label="Kostnad per søk" std="1 kreditt" pro="1 kreditt" />
              <Row label="Søk etter område og bransje" std pro />
              <Row label="Kontaktinfo og personlige e-postutkast" std pro />
              <Row label="Søkehistorikk og lagrede søk" std pro />
              <Row label="Større søkeradius og flere treff per søk" std={false} pro />
              <Row label="Fjerner bedrifter du allerede har funnet" std={false} pro />
              <Row label="Rangering: bedrifter med e-post og nærhet først" std={false} pro />
              <Row label="Hvordan" std="Inkludert" pro="Låses opp med 300, 600 eller 1200 kreditter" />
            </tbody>
          </table>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-12">
        <h2 className="font-heading text-3xl font-extrabold">Priser – engangskjøp</h2>
        <p className="mt-2 text-stone-600">Ingen abonnement. Jo større pakke, jo billigere per søk – og fra 300 kreditter får du Pro.</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div className="rounded-2xl border-2 border-dashed border-stone-300 bg-white p-6" data-testid="pf-package-free">
            <div className="text-sm font-bold text-stone-500">PRØV GRATIS</div>
            <div className="mt-2 font-heading text-3xl font-extrabold">0 kr</div>
            <p className="mt-2 text-sm text-stone-600">{p?.free_searches ?? 5} søk, opptil {p?.free_limit ?? 20} bedrifter per søk. Ingen kort.</p>
          </div>
          {(p?.packages || []).map((k) => <PackageCard key={k.id} k={k} std={std} base={p.packages[0]} />)}
        </div>
        <p className="mt-3 text-xs text-stone-500">Maksimale tall forutsetter at hvert søk finner {std} bedrifter. Antall unike og relevante treff er ikke garantert.</p>
        <Link to="/login?mode=register" className="mt-6 inline-block rounded-full bg-[#2F5D50] px-7 py-3.5 font-bold text-white hover:bg-[#244a40]">Opprett konto og start gratis</Link>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-12">
        <h2 className="font-heading text-3xl font-extrabold">Vanlige spørsmål</h2>
        {FAQ.map(([q, a]) => <div key={q} className="mt-5"><h3 className="font-bold">{q}</h3><p className="text-stone-600">{a}</p></div>)}
      </section>
    </main>
  );
}
